import {
  useErrValidationFailedContext,
  useWriteValidationError,
} from "@/shared/lib/errors/err-validation-failed-context";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { Separator } from "@/shared/components/ui/separator";
import {
  DocumentNotReadyAlert,
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsDownload,
  DocumentSheetActionsHeader,
  DocumentSheetActionsSetImmutableMutation,
  DocumentSheetContent,
  DocumentSheetHeader,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { Download } from "lucide-react";
import { useDownloadPdf } from "@/shared/lib/utilities/download-pdf";
import { useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  useRltCertificatePdfDownloadMutation,
  useLazyRltCertificatePdfPreviewQuery,
  useRltCertificateUpdateStateMutation,
} from "@/services/backend/rlt/certificates/api/certificate-api";
import { DocumentState } from "@/services/backend/documents/types/state";

export function CertificatePdf({
  certificateId,
  immutable = false,
}: {
  certificateId: string;
  immutable?: boolean;
}) {
  const previewRequest = {
    id: certificateId,
  };
  const [loadPreview, { data, isLoading, isFetching, error }] =
    useLazyRltCertificatePdfPreviewQuery();

  const { write } = useErrValidationFailedContext();
  const { user } = useAuth();

  // The preview is rendered by the backend. It is loaded when the sheet
  // opens, not on every auto-save. Reopening shows the last one until the
  // fresh one arrives.
  const onOpenChange = (open: boolean) => {
    if (open) {
      loadPreview(previewRequest);
    }
  };

  return (
    <DocumentSheet onOpenChange={onOpenChange}>
      <DocumentSheetTrigger asChild>
        <Button variant="outline">PDF</Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="max-h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>{t("Download PDF")}</DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <DocumentSheetPreview
            objectURL={data?.objectURL}
            isLoading={isLoading}
            isFetching={isFetching}
            error={error}
          />
          <DocumentSheetActions>
            <DocumentSheetActionsHeader
              title={t("Zertifikat")}
              documentNumber={null}
            />
            <DocumentSheetActionsContent>
              {!immutable && (
                <>
                  <DocumentNotReadyAlert document={t("Zertifikat")} />
                  <DocumentSheetActionsSetImmutableMutation
                    request={{
                      id: certificateId,
                      state: DocumentState.Immutable,
                      stateSetBy: user?.id || "",
                    }}
                    mutation={useRltCertificateUpdateStateMutation}
                    description={t(
                      "Das Zertifikat wird festgeschrieben. Danach kann das Zertifikat nicht mehr geändert werden.",
                    )}
                    onSuccess={() => loadPreview(previewRequest)}
                    onError={(err) => write(err)}
                  />
                  <Separator />
                </>
              )}
              <DocumentSheetActionsDownload>
                <DownloadDocumentButton certificateId={certificateId} />
              </DocumentSheetActionsDownload>
            </DocumentSheetActionsContent>
          </DocumentSheetActions>
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function DownloadDocumentButton({ certificateId }: { certificateId: string }) {
  const [open, setOpen] = useState(false);
  const [download, { isLoading, error, data, reset }] =
    useRltCertificatePdfDownloadMutation();

  useDownloadPdf(data, () => {
    setOpen(false);
    reset();
  });

  useWriteValidationError(error, {
    onWrite: () => {
      setOpen(false);
      reset();
    },
  });

  const doDownload = () => {
    if (isLoading) {
      return;
    }

    download({ id: certificateId });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="justify-start">
          <Download className="mr-4 h-5 w-5" />
          <span>{t("Zertifikat")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Zertifikat herunterladen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Wenn ein Zertifikat heruntergeladen wird, wird es festgeschrieben. Esx kann dann nicht mehr geändert werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doDownload} disabled={isLoading}>
            {isLoading ? (
              <Download className="mr-4 h-5 w-5 animate-bounce" />
            ) : (
              <Download className="mr-4 h-5 w-5" />
            )}
            <span>{t("Herunterladen")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
